import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, EmptyBox, timeAgo } from "./shared";
import { BusinessDnaPanel } from "@/components/admin/BusinessDnaPanel";
import { Mail, Phone, Building2, Target, Trash2, TrendingUp, LayoutTemplate, Sparkles, MousePointerClick, Clock, Fingerprint } from "lucide-react";

const STATUSES = ["new", "contacted", "qualified", "won", "lost"];
const STATUS_LABEL = { new: "New", contacted: "Contacted", qualified: "Qualified", won: "Won", lost: "Lost" };
const STATUS_CLS = {
  new: "bg-gold/15 text-gold border-gold/30",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  qualified: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  won: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  lost: "bg-white/5 text-gray-500 border-white/10",
};
const ACTION_LABEL = { assessment: "Continued to assessment", save: "Saved project", schedule: "Requested consultation" };

function Bar({ rows, icon: Icon, title, empty }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="bg-navy-800 border border-white/8 p-5">
      <div className="flex items-center gap-2 text-white font-medium mb-4"><Icon size={16} className="text-gold" /> {title}</div>
      {rows.length === 0 ? (
        <div className="text-sm text-gray-600 py-6 text-center">{empty}</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.label}>
              <div className="flex justify-between text-xs mb-1"><span className="text-gray-300 truncate pr-2">{r.label}</span><span className="text-gold font-semibold">{r.count}</span></div>
              <div className="h-1.5 bg-white/5"><div className="h-full bg-gold" style={{ width: `${(r.count / max) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CustomBuildInquiries() {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [dnaEmail, setDnaEmail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/inquiries${filter ? `?status=${filter}` : ""}`),
      api.get("/admin/inquiries/insights"),
    ]).then(([a, b]) => { setData(a.data); setInsights(b.data); })
      .catch(() => toast.error("Failed to load inquiries"))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/inquiries/${id}/status`, { status });
      load();
    } catch {
      toast.error("Update failed");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    await api.delete(`/admin/inquiries/${id}`).catch(() => toast.error("Delete failed"));
    toast.success("Inquiry deleted");
    load();
  };

  return (
    <div>
      <Head title="Custom-Build Inquiries" desc="Leads captured from the marketplace 'Get this built for my business' funnel." />

      {/* Funnel Insights */}
      {insights && (
        <div className="mb-8" data-testid="funnel-insights">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {[
              { label: "Total inquiries", value: insights.total, icon: Sparkles },
              { label: "Qualified", value: insights.qualified, icon: Target },
              { label: "Won", value: insights.won, icon: TrendingUp },
              { label: "Win rate", value: `${insights.win_rate}%`, icon: TrendingUp },
            ].map((m) => (
              <div key={m.label} className="bg-navy-800 border border-white/8 p-4">
                <div className="text-2xl font-heading text-white">{m.value}</div>
                <div className="text-xs uppercase tracking-wider text-gray-500 mt-1">{m.label}</div>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            <Bar rows={insights.top_templates} icon={LayoutTemplate} title="Top templates" empty="No inquiries yet." />
            <Bar rows={insights.top_industries} icon={Building2} title="Top industries" empty="No industry data yet." />
            <Bar rows={insights.cta_performance} icon={MousePointerClick} title="CTA source" empty="No CTA data yet." />
            <Bar rows={insights.action_performance} icon={Target} title="Next-step chosen" empty="No data yet." />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="inquiry-filters">
        {[["", "All"], ...STATUSES.map((s) => [s, STATUS_LABEL[s]])].map(([k, l]) => (
          <button key={k || "all"} onClick={() => setFilter(k)} data-testid={`filter-${k || "all"}`}
            className={`px-3.5 py-1.5 text-xs border transition-colors ${filter === k ? "bg-gold text-navy border-gold" : "text-gray-400 border-white/10 hover:text-white"}`}>
            {l}{data?.counts && (k ? ` (${data.counts[k] ?? 0})` : ` (${data.counts.all ?? 0})`)}
          </button>
        ))}
      </div>

      {loading ? <Loader /> : !data?.inquiries?.length ? (
        <EmptyBox text="No inquiries yet. They'll appear here the moment a visitor uses the custom-build funnel." />
      ) : (
        <div className="space-y-3" data-testid="inquiry-list">
          {(() => {
            const staleCount = data.inquiries.filter((q) => q.is_stale).length;
            return staleCount > 0 ? (
              <div className="flex items-center gap-2.5 bg-amber-400/10 border border-amber-400/30 text-amber-200 px-4 py-3 text-sm" data-testid="followup-banner">
                <Clock size={16} /> {staleCount} lead{staleCount === 1 ? "" : "s"} {staleCount === 1 ? "has" : "have"} been waiting more than 48 hours — reach out before they go cold.
              </div>
            ) : null;
          })()}
          {data.inquiries.map((q) => (
            <div key={q.id} data-testid={`inquiry-${q.id}`} className={`bg-navy-800 border p-5 ${q.is_stale ? "border-amber-400/40" : "border-white/8"}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <span className="text-white font-medium">{q.name}</span>
                    <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border ${STATUS_CLS[q.status]}`}>{STATUS_LABEL[q.status]}</span>
                    {q.is_stale && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 border border-amber-400/40 bg-amber-400/15 text-amber-200 flex items-center gap-1" data-testid={`followup-${q.id}`}><Clock size={11} /> Follow up</span>}
                    {q.template_title && <span className="text-[11px] text-gold flex items-center gap-1"><LayoutTemplate size={12} /> {q.template_title}</span>}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-400 mt-2">
                    <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 hover:text-gold"><Mail size={13} /> {q.email}</a>
                    {q.phone && <span className="flex items-center gap-1.5"><Phone size={13} /> {q.phone}</span>}
                    {q.business_name && <span className="flex items-center gap-1.5"><Building2 size={13} /> {q.business_name}</span>}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mt-2">
                    {q.industry && <span>Industry: <span className="text-gray-300">{q.industry}</span></span>}
                    {q.budget && <span>Budget: <span className="text-gray-300">{q.budget}</span></span>}
                    {q.timeline && <span>Timeline: <span className="text-gray-300">{q.timeline}</span></span>}
                    {q.preferred_contact && <span>Contact via: <span className="text-gray-300">{q.preferred_contact}</span></span>}
                    {q.preferred_time && <span>Best time: <span className="text-gray-300">{q.preferred_time}</span></span>}
                  </div>
                  {q.goals && <p className="text-sm text-gray-300 mt-2.5 italic">"{q.goals}"</p>}
                  <div className="text-[11px] text-gray-600 mt-2">
                    {ACTION_LABEL[q.action] || q.action} · via {q.cta_source || "n/a"} · {timeAgo(q.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setDnaEmail(q.email)} data-testid={`dna-view-${q.id}`}
                    className="flex items-center gap-1.5 text-xs border border-gold/40 text-gold px-2.5 py-2 hover:bg-gold/10 transition-colors" title="See what we know about this business">
                    <Fingerprint size={14} /> What we know
                  </button>
                  <select value={q.status} onChange={(e) => setStatus(q.id, e.target.value)} data-testid={`inquiry-status-${q.id}`}
                    className="bg-white/5 border border-white/15 text-white text-xs px-2.5 py-2 focus:border-gold focus:outline-none">
                    {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                  <button onClick={() => remove(q.id)} data-testid={`inquiry-delete-${q.id}`} className="text-gray-500 hover:text-red-400 p-2"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {dnaEmail && <BusinessDnaPanel email={dnaEmail} onClose={() => setDnaEmail(null)} />}
    </div>
  );
}
